import {createContext, type ReactNode, useCallback, useContext, useMemo, useState} from 'react';
import {createSearchParams, useSearchParams} from 'react-router';

type SearchParamsTuple = ReturnType<typeof useSearchParams>;
type SetSearchParams = SearchParamsTuple[1];

const RoadmapSearchParamsContext = createContext<SearchParamsTuple | null>(null);

/**
 * Roadmap state lives in URL search params. Hosts that embed the Roadmap (e.g.
 * /workstreams) wrap it in this provider so that state is kept
 * in memory instead of reading / rewriting the host page's URL.
 */
export const RoadmapSearchParamsProvider = ({
  value,
  children,
}: {
  value: SearchParamsTuple;
  children: ReactNode;
}) => (
  <RoadmapSearchParamsContext.Provider value={value}>
    {children}
  </RoadmapSearchParamsContext.Provider>
);

// Router-backed by default; in-memory when rendered under RoadmapSearchParamsProvider.
export const useRoadmapSearchParams = (): SearchParamsTuple => {
  const routerSearchParams = useSearchParams();
  return useContext(RoadmapSearchParamsContext) ?? routerSearchParams;
};

// In-memory stand-in for useSearchParams with the same setter signature.
export const useMemorySearchParams = (init?: string): SearchParamsTuple => {
  const [searchParams, setState] = useState(() => createSearchParams(init));
  const setSearchParams = useCallback<SetSearchParams>((nextInit) => {
    setState((prev) =>
      createSearchParams(typeof nextInit === 'function' ? nextInit(prev) : nextInit)
    );
  }, []);
  return useMemo(() => [searchParams, setSearchParams], [searchParams, setSearchParams]);
};
